import {
  BaseEditor,
  Descendant,
  Editor,
  Element as SlateElement,
  Node as SlateNode,
  Transforms,
} from 'slate';
import { HistoryEditor } from 'slate-history';
import { ReactEditor } from 'slate-react';

import { ImageElement, ImagesEditor } from './image';
import { LinkElement } from './link';

/** Represents the Text node format and it's available options */
export type CustomText = {
  text: string;
  bold?: true;
  italic?: true;
  underline?: true;
};

/** Union of all custom text options */
export type MarkFormat = Exclude<keyof CustomText, 'text'>;

/** Represents the heading element */
export type HeadingElement = { type: 'heading-two'; children: CustomText[] };

/** Represents the paragraph element */
export type ParagraphElement = { type: 'paragraph'; children: SlateNode[] };

/** Describes all the possible elements in our editor */
type CustomElement =
  | HeadingElement
  | ParagraphElement
  | ImageElement
  | LinkElement;

// Extend the slate type definitions with our setup
declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor & HistoryEditor & ImagesEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

/** An empty slate document */
export const EmptyDocument: Descendant[] = [
  {
    type: 'paragraph',
    children: [
      {
        text: '',
      },
    ],
  },
];

/** Toggle the passed in text format on the currently selected text node */
export function toggleMark(editor: Editor, format: MarkFormat): void {
  isMarkActive(editor, format)
    ? Editor.removeMark(editor, format)
    : Editor.addMark(editor, format, true);
}

/**
 * Check if the selected block matches the passed in format
 *
 * @param editor Instance of slate editor
 * @param format Format you want to check is active
 * @returns
 */
export function isBlockActive(
  editor: Editor,
  format: 'heading-two' | 'paragraph',
): boolean {
  const [match] = Editor.nodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
  });
  return !!match;
}

/**
 * Checks if the text node has the passed format active
 *
 * @param editor Instance of slate editor
 * @param format Mark/text format you want to check
 * @returns
 */
export function isMarkActive(editor: Editor, format: MarkFormat): boolean {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
}

/**
 * Switch the current elment to the passed in format, acts as a toggle between
 * paragraph and heading-two
 *
 * @param editor Instance of slate editor
 * @param format
 */
export function toggleBlock(
  editor: Editor,
  format: 'paragraph' | 'heading-two',
): void {
  const isActive = isBlockActive(editor, format);
  const newProperties: Partial<SlateElement> = {
    type: isActive ? 'paragraph' : format,
  };
  Transforms.setNodes(editor, newProperties);
}
